import React, { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n';
import versionInfo from '../version.json';

// Public legal pages: /terms, /privacy, /refund. The documents themselves are
// kept in English only (the binding version); the surrounding chrome follows
// the user's language/theme like the rest of the site.

const LAST_UPDATED = '30 September 2026';
const COMPANY = 'Alaganu Enterprise';
const CONTACT_EMAIL = 'marketing@alaganu.com';

const TERMS = {
  title: 'Terms of Service',
  intro: `These Terms of Service ("Terms") govern your use of Invio, the business document management service available at invio.my and through the Invio mobile applications (the "Service"), operated by ${COMPANY} ("we", "us"). By creating an account or using the Service you agree to these Terms. If you do not agree, do not use the Service.`,
  sections: [
    {
      h: '1. Your account',
      p: [
        'You must provide accurate information when registering and keep it up to date. You are responsible for all activity under your account and for keeping your password confidential. Notify us immediately if you suspect unauthorised access.',
        'The Service is intended for business use by persons aged 18 or older. One person or business per account unless we agree otherwise in writing.',
      ],
    },
    {
      h: '2. Free trial, subscriptions and billing',
      p: [
        'New accounts receive a free trial (currently 30 days, no credit card required). When the trial ends, a short grace period may apply, after which a paid subscription is required to continue using the Service.',
        'Paid subscriptions are billed in advance on a monthly or yearly basis through our payment processor, Stripe, and renew automatically until cancelled. You can cancel at any time from the Subscription page; cancellation takes effect at the end of the current billing period.',
        'If a renewal payment fails, we will notify you and retry the charge. A short grace period applies during which you keep access; if payment is still unsuccessful, access to paid features may be suspended until payment is made.',
        'We may change subscription prices with reasonable advance notice. Price changes apply from your next billing period. Refunds are handled according to our Refund Policy.',
      ],
    },
    {
      h: '3. Acceptable use',
      p: [
        'You agree to use the Service only for lawful purposes. In particular, you must not: issue false, misleading or fraudulent documents; use the Service to infringe the rights of others; attempt to gain unauthorised access to the Service or its systems; interfere with the operation of the Service; or resell, sublicense or white-label the Service without our written consent.',
        'We may suspend or terminate accounts that breach these Terms, abuse the Service, or create risk or legal exposure for us or other users.',
      ],
    },
    {
      h: '4. Your content and data',
      p: [
        'You retain ownership of the data you enter into the Service — your company profile, clients, items, documents and uploaded files ("Your Content"). You grant us a limited licence to host, process, back up and display Your Content solely to provide and support the Service.',
        'Features that use AI (such as document import and data extraction) process the files you upload in order to return extracted data to you. Your Content is not used to train AI models.',
        'You are responsible for the accuracy and legality of Your Content, including the documents you issue to your own clients. You can export your documents at any time.',
      ],
    },
    {
      h: '5. Tax compliance and e-Invoice',
      p: [
        'The Service can help you prepare and submit documents to tax authorities (for example LHDN MyInvois e-Invoices in Malaysia). We provide the tooling only: you remain solely responsible for the accuracy, completeness and timeliness of your tax and regulatory submissions. Nothing in the Service constitutes tax, legal or accounting advice.',
      ],
    },
    {
      h: '6. Availability and changes to the Service',
      p: [
        'We work hard to keep the Service available and your data safe, including routine backups, but the Service is provided "as is" and "as available" without warranties of any kind, whether express or implied. We may add, change or remove features from time to time.',
        'We may perform maintenance that temporarily interrupts the Service and will try to minimise disruption.',
      ],
    },
    {
      h: '7. Termination',
      p: [
        'You may stop using the Service and cancel your subscription at any time. We may suspend or terminate your access if you materially breach these Terms and (where practical) after notice giving you an opportunity to remedy the breach.',
        'After an account is closed, we may delete Your Content following a reasonable retention window. Export anything you need before closing your account.',
      ],
    },
    {
      h: '8. Limitation of liability',
      p: [
        'To the maximum extent permitted by law, we are not liable for any indirect, incidental, special or consequential damages, or for loss of profits, revenue, data or business opportunities, arising from your use of the Service.',
        'To the maximum extent permitted by law, our total aggregate liability for all claims relating to the Service is limited to the amount you paid us for the Service in the 12 months before the event giving rise to the claim.',
      ],
    },
    {
      h: '9. Governing law',
      p: [
        'These Terms are governed by the laws of Malaysia, and the courts of Malaysia have exclusive jurisdiction over any dispute arising from them, without prejudice to any mandatory consumer protections that apply in your country of residence.',
      ],
    },
    {
      h: '10. Changes to these Terms',
      p: [
        'We may update these Terms from time to time. If a change is material we will notify you (for example by email or an in-app notice) before it takes effect. Continuing to use the Service after a change takes effect means you accept the updated Terms.',
      ],
    },
    {
      h: '11. Contact',
      p: [
        `Questions about these Terms? Contact us at ${CONTACT_EMAIL}.`,
      ],
    },
  ],
};

const PRIVACY = {
  title: 'Privacy Policy',
  intro: `This Privacy Policy explains how ${COMPANY} ("we", "us") collects, uses and protects personal data when you use Invio (invio.my and the Invio mobile applications). We process personal data in accordance with the Malaysian Personal Data Protection Act 2010 (PDPA).`,
  sections: [
    {
      h: '1. Data we collect',
      p: [
        'Account data — your name, email address and a hashed password (we never store your password in plain text).',
        'Company data — your business profile: company name, address, logo, tax identifiers and document settings.',
        'Business content — the clients, items, documents and files you create or upload in order to use the Service.',
        'Billing data — your subscription status and payment history. Card payments are processed by Stripe; we do not receive or store your full card number.',
        'Technical data — log data such as IP address, browser type and timestamps, used for security and troubleshooting, and preferences (such as language and theme) stored in your browser.',
      ],
    },
    {
      h: '2. How we use your data',
      p: [
        'To provide and operate the Service: storing your documents, generating PDFs, sending the emails you trigger (such as sending an invoice to a client), and syncing your data across devices.',
        'To manage your account: verification emails, password resets, billing receipts, renewal and payment-failure notices, and replies to your support requests.',
        'To keep the Service secure: fraud prevention, abuse detection and audit logging.',
        'We do not sell your personal data, and we do not use your data for third-party advertising.',
      ],
    },
    {
      h: '3. AI features',
      p: [
        'When you use AI-powered features (such as importing a price list or extracting document data), the file you upload is processed to extract the data and return it to you. Uploaded content is not used to train AI models.',
      ],
    },
    {
      h: '4. Sharing with third parties',
      p: [
        'We share data only with service providers needed to run Invio: Stripe (payment processing), our email delivery provider (transactional emails), our hosting and database providers (storing the Service data), and — only when you choose to submit an e-Invoice — the relevant tax authority platform (such as LHDN MyInvois).',
        'These providers process data on our behalf under their own security and privacy obligations. We may also disclose data where required by law.',
      ],
    },
    {
      h: '5. Cookies and local storage',
      p: [
        'Invio uses browser local storage for essential purposes only: keeping you signed in (authentication token) and remembering preferences such as language, theme and sidebar state. We do not use third-party advertising or tracking cookies.',
      ],
    },
    {
      h: '6. Data retention',
      p: [
        'We keep your data while your account is active. If your account is closed or deleted, we delete or anonymise your data after a reasonable retention window, except where we must keep records to comply with legal, tax or accounting obligations. Backup copies are purged on a rolling schedule.',
      ],
    },
    {
      h: '7. Security',
      p: [
        'Data is encrypted in transit (HTTPS), passwords are stored hashed, and access to production data is restricted. No system is perfectly secure, so we also encourage you to use a strong, unique password.',
      ],
    },
    {
      h: '8. Your rights',
      p: [
        `Under the PDPA you may request access to, correction of, or deletion of your personal data, and you may withdraw consent to processing (which may mean we can no longer provide the Service to you). You can edit most data directly in the app, export your documents at any time, or contact us at ${CONTACT_EMAIL} to exercise these rights.`,
      ],
    },
    {
      h: '9. Children',
      p: [
        'The Service is intended for business use and is not directed at children under 18. We do not knowingly collect personal data from children.',
      ],
    },
    {
      h: '10. Changes to this policy',
      p: [
        'We may update this Privacy Policy from time to time. Material changes will be notified by email or an in-app notice. The "Last updated" date at the top shows the current version.',
      ],
    },
    {
      h: '11. Contact',
      p: [
        `For any privacy question or request, contact us at ${CONTACT_EMAIL}.`,
      ],
    },
  ],
};

const REFUND = {
  title: 'Refund Policy',
  intro: `We want you to be confident before paying for Invio — that is what the free trial is for. This policy explains how billing and refunds work for subscriptions purchased from ${COMPANY}.`,
  sections: [
    {
      h: '1. Try before you pay',
      p: [
        'Every new account includes a free trial with full access and no credit card required. Please use the trial to make sure Invio fits your business before subscribing.',
      ],
    },
    {
      h: '2. Cancelling your subscription',
      p: [
        'You can cancel at any time from the Subscription page. Your plan stays active until the end of the period you have already paid for, and you will not be charged again. We do not provide prorated refunds for the unused remainder of a billing period.',
      ],
    },
    {
      h: '3. When we do refund',
      p: [
        'We will refund you in full if: you were charged twice for the same period (duplicate charge); you were charged after you had already cancelled; or a billing error on our side caused an incorrect charge.',
        `If you subscribed to a yearly plan and quickly realise it is not right for you, contact us within 14 days of the charge — provided the account has had only minimal use in that time, we will refund the yearly payment. Requests are reviewed case by case.`,
      ],
    },
    {
      h: '4. How refunds are paid',
      p: [
        'Approved refunds are issued to the original payment method through Stripe, typically within 5–10 business days depending on your bank.',
      ],
    },
    {
      h: '5. How to request a refund',
      p: [
        `Email ${CONTACT_EMAIL} from your account email address (or open a support case in the app) with your account email and the date and amount of the charge. We aim to respond within 2 business days.`,
      ],
    },
  ],
};

const DOCS = { terms: TERMS, privacy: PRIVACY, refund: REFUND };

const TABS = [
  { doc: 'terms',   to: '/terms',   labelKey: 'footer.terms' },
  { doc: 'privacy', to: '/privacy', labelKey: 'footer.privacy' },
  { doc: 'refund',  to: '/refund',  labelKey: 'footer.refund' },
];

export default function Legal({ doc }) {
  const { theme, toggle: toggleTheme } = useTheme();
  const { t, isRTL } = useT();
  const content = DOCS[doc] || TERMS;

  useEffect(() => { window.scrollTo(0, 0); }, [doc]);

  // Per-page SEO: distinct title, description and canonical so these pages are
  // indexed on their own instead of as duplicates of the homepage. Restored to
  // the homepage values when leaving a legal page.
  useEffect(() => {
    const setMeta = (selector, attr, value) => {
      const el = document.head.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    const HOME_TITLE = 'Invio — AI Invoice, Quotation & Receipt Software | Extract Items from PDF, Excel & CSV';
    document.title = `${content.title} — Invio`;
    setMeta('link[rel="canonical"]', 'href', `https://invio.my/${doc}`);
    setMeta('meta[name="description"]', 'content', `${content.title} for Invio — professional business document software (invoices, quotations, receipts). ${content.intro.slice(0, 130)}`);
    return () => {
      document.title = HOME_TITLE;
      setMeta('link[rel="canonical"]', 'href', 'https://invio.my/');
    };
  }, [doc, content]);

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      {/* Header */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'var(--card-bg)', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          maxWidth: 860, margin: '0 auto', padding: '12px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'baseline', textDecoration: 'none' }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: '#F59E0B', letterSpacing: '-1.5px' }}>I</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)', letterSpacing: '-1px' }}>nvio</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <NavLink key={tab.doc} to={tab.to} style={({ isActive }) => ({
                fontSize: 13, fontWeight: 600, textDecoration: 'none',
                padding: '6px 12px', borderRadius: 8,
                color: isActive ? '#1A3C6E' : 'var(--text-muted)',
                background: isActive ? 'var(--accent)' : 'transparent',
              })}>
                {t(tab.labelKey)}
              </NavLink>
            ))}
            <button onClick={toggleTheme} aria-label="Toggle theme" style={{
              background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 15,
              color: 'var(--text-muted)', padding: 4,
            }}>{theme === 'dark' ? '☀' : '🌙'}</button>
            <Link to="/" style={{
              fontSize: 13, fontWeight: 600, textDecoration: 'none', color: 'var(--heading)',
              border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px',
            }}>
              {isRTL ? '→' : '←'} {t('nav.home')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Document — legal text is English-only, keep it LTR even in Arabic UI */}
      <main dir="ltr" style={{ maxWidth: 860, margin: '0 auto', padding: '48px 24px 64px', textAlign: 'left' }}>
        <h1 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: 'var(--heading)', letterSpacing: '-1px', marginBottom: 8 }}>
          {content.title}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-subtle)', marginBottom: 28 }}>
          Last updated: {LAST_UPDATED} · Invio · invio.my · operated by {COMPANY}
        </p>
        <p style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--text)', marginBottom: 32 }}>
          {content.intro}
        </p>

        {content.sections.map(s => (
          <section key={s.h} style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--heading)', marginBottom: 10 }}>{s.h}</h2>
            {s.p.map((para, i) => (
              <p key={i} style={{ fontSize: 14.5, lineHeight: 1.75, color: 'var(--text-muted)', marginBottom: 10 }}>
                {para}
              </p>
            ))}
          </section>
        ))}

        <div style={{
          marginTop: 40, padding: '16px 20px', background: 'var(--hover-bg)',
          border: '1px solid var(--border)', borderRadius: 12,
          fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6,
        }}>
          Questions? Email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--blue)', fontWeight: 600 }}>{CONTACT_EMAIL}</a>
          {' '}or message us on WhatsApp at <a href="https://wa.me/60106650171" target="_blank" rel="noreferrer" style={{ color: 'var(--blue)', fontWeight: 600 }}>+60 10 665 0171</a>.
        </div>
      </main>

      <footer style={{ borderTop: '1px solid var(--border)', padding: '20px 24px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
          <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>v{versionInfo.version}</span>
        </div>
      </footer>
    </div>
  );
}
